import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "../../context/AdminContext";
import { toast } from "sonner";
import axios from "axios";
import {
  HelpCircle, ArrowLeft, Plus, Edit2, Trash2, Save, X, LogOut, Filter
} from "lucide-react";
import { API_BASE_URL } from "../../lib/config";

const API_URL = API_BASE_URL;

const CATEGORIES = [
  { id: "staar", name: "STAAR Test" },
  { id: "sat", name: "SAT" },
  { id: "act", name: "ACT" },
  { id: "asvab", name: "ASVAB" },
  { id: "citizenship", name: "US Citizenship" }
];

const AdminQuestions = () => {
  const { token, logout } = useAdmin();
  const [questions, setQuestions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    category: "sat",
    question: "",
    options: ["", "", "", ""],
    correct_answer: 0,
    explanation: "",
    difficulty: "medium",
    topic: ""
  });

  useEffect(() => {
    fetchQuestions();
  }, [selectedCategory]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const params = selectedCategory ? `?category=${selectedCategory}` : "";
      const response = await axios.get(`${API_URL}/admin/questions${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data.questions);
    } catch (error) {
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      setFormData({
        category: question.category,
        question: question.question,
        options: question.options,
        correct_answer: question.correct_answer,
        explanation: question.explanation,
        difficulty: question.difficulty || "medium",
        topic: question.topic || ""
      });
    } else {
      setEditingQuestion(null);
      setFormData({
        category: selectedCategory || "sat",
        question: "",
        options: ["", "", "", ""],
        correct_answer: 0,
        explanation: "",
        difficulty: "medium",
        topic: ""
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingQuestion(null);
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.question || formData.options.some(o => !o) || !formData.explanation) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      if (editingQuestion && editingQuestion.source === "database") {
        await axios.put(
          `${API_URL}/admin/questions/${editingQuestion.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Question updated");
      } else {
        await axios.post(
          `${API_URL}/admin/questions`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Question created");
      }
      handleCloseModal();
      fetchQuestions();
    } catch (error) {
      toast.error("Failed to save question");
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm("Delete this question?")) return;
    
    try {
      await axios.delete(`${API_URL}/admin/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Question deleted");
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete question");
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-bold text-white flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-lime-400" />
                Question Management
              </h1>
              <p className="text-sm text-slate-400">{questions.length} questions</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 px-4 py-2 bg-lime-500 hover:bg-lime-600 text-white font-bold rounded-xl transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Question
            </button>
            <button
              onClick={() => { logout(); toast.success("Logged out"); }}
              className="p-2 text-slate-400 hover:text-white transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Category Filter */}
        <div className="mb-6 flex items-center gap-4">
          <Filter className="w-5 h-5 text-slate-400" />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="h-10 px-4 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-indigo-500 focus:outline-none"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Questions List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-lime-500 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No questions found</div>
          ) : (
            questions.map((q) => (
              <div key={q.id} className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                        q.source === "sample" 
                          ? "bg-slate-600 text-slate-300" 
                          : "bg-lime-500/20 text-lime-400"
                      }`}>
                        {q.source === "sample" ? "SAMPLE" : "CUSTOM"}
                      </span>
                      <span className="px-2 py-1 bg-indigo-500/20 text-indigo-400 text-xs font-bold rounded-full uppercase">
                        {q.category}
                      </span>
                      <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                        q.difficulty === "easy" ? "bg-green-500/20 text-green-400" :
                        q.difficulty === "hard" ? "bg-red-500/20 text-red-400" :
                        "bg-amber-500/20 text-amber-400"
                      }`}>
                        {q.difficulty}
                      </span>
                    </div>
                    <p className="text-white font-medium">{q.question}</p>
                  </div>
                  {q.source !== "sample" && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenModal(q)}
                        className="p-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(q.id)}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  {q.options.map((opt, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-xl text-sm ${
                        idx === q.correct_answer
                          ? "bg-green-500/20 text-green-400 border border-green-500/30"
                          : "bg-slate-700/50 text-slate-300"
                      }`}
                    >
                      {String.fromCharCode(65 + idx)}. {opt}
                    </div>
                  ))}
                </div>

                <div className="text-sm text-slate-400 bg-slate-700/50 p-3 rounded-xl">
                  <strong className="text-slate-300">Explanation:</strong> {q.explanation}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Question Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <h3 className="font-bold text-white text-lg">
                {editingQuestion ? "Edit Question" : "Add New Question"}
              </h3>
              <button onClick={handleCloseModal} className="text-slate-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 font-medium mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full h-10 px-4 bg-slate-700 border border-slate-600 rounded-xl text-white focus:border-indigo-500 focus:outline-none"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-300 font-medium mb-2">Difficulty</label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full h-10 px-4 bg-slate-700 border border-slate-600 rounded-xl text-white focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-2">Topic (optional)</label>
                <input
                  type="text"
                  value={formData.topic}
                  onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                  className="w-full h-10 px-4 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:border-indigo-500 focus:outline-none"
                  placeholder="e.g., Algebra, Reading Comprehension"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-2">Question</label>
                <textarea
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:border-indigo-500 focus:outline-none resize-none"
                  rows={3}
                  placeholder="Enter the question..."
                />
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-2">Options</label>
                <div className="space-y-2">
                  {formData.options.map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="correct_answer"
                        checked={formData.correct_answer === idx}
                        onChange={() => setFormData({ ...formData, correct_answer: idx })}
                        className="w-4 h-4 text-lime-500"
                      />
                      <span className="text-slate-400 w-6">{String.fromCharCode(65 + idx)}.</span>
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) => handleOptionChange(idx, e.target.value)}
                        className="flex-1 h-10 px-4 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:border-indigo-500 focus:outline-none"
                        placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-sm text-slate-400 mt-2">Select the correct answer</p>
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-2">Explanation</label>
                <textarea
                  value={formData.explanation}
                  onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:border-indigo-500 focus:outline-none resize-none"
                  rows={2}
                  placeholder="Explain why this is the correct answer..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-lime-500 hover:bg-lime-600 text-white font-bold rounded-xl transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingQuestion ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuestions;
